import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";

/**
 * Types a fixed string out character by character.
 *
 * Distinct from `use-streaming-typewriter`, which chases a growing buffer from
 * an SSE stream and therefore never stops its timer. Onboarding copy is known
 * up front, so this one runs to the end and stops — important when a long
 * conversation has a dozen messages mounted at once.
 *
 * Returns the text to render plus whether it's still typing, so callers can
 * hold a caret or delay the step widget until the line has landed.
 */
export function useTypewriter({
  text,
  /** Milliseconds per character. */
  speed = 12,
  /** Skip the animation entirely — used when replaying a saved transcript. */
  instant = false,
  onDone,
}: {
  text: string;
  speed?: number;
  instant?: boolean;
  onDone?: () => void;
}) {
  const shouldReduce = useReducedMotion();
  const skip = instant || shouldReduce;

  const [index, setIndex] = useState(skip ? text.length : 0);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    if (skip) {
      setIndex(text.length);
      onDoneRef.current?.();
      return;
    }

    setIndex(0);
    let i = 0;
    let cancelled = false;

    const tick = () => {
      if (cancelled) return;
      i += 1;
      setIndex(i);

      if (i >= text.length) {
        onDoneRef.current?.();
        return;
      }

      // Pause a beat on sentence breaks so the line reads like speech rather
      // than a ticker.
      const char = text[i - 1];
      const pause = /[.!?]/.test(char) ? speed * 12 : /[,;:—]/.test(char) ? speed * 6 : speed;
      timer = setTimeout(tick, pause);
    };

    let timer = setTimeout(tick, speed);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [text, speed, skip]);

  return {
    displayed: text.slice(0, index),
    isTyping: !skip && index < text.length,
  };
}
